import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { Stack } from "@mui/material";
import { PortfolioGroupedItem } from "../../models/PortfolioGroupedItem";
import { NumericField } from "../../controls";
import { DraggableDialogPaper } from "../DraggableDialogPaper";
import { DraggableDialogTitle } from "../DraggableDialogTitle";
import { ControlGroup } from "../order-manager-dialog/views/ControlGroup";
import { numberFormatters } from "../../../shared/utils/numberFormatters";

export function PositionManagerDialog({
    isOpen,
    setIsOpen,
    position,
    submitAction,
}: {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    position: PortfolioGroupedItem | null;
    submitAction: (customImpliedVolatility: number) => Promise<void>;
}) {
    const [customImpliedVolatility, setCustomImpliedVolatility] = React.useState<number>(0);

    React.useEffect(() => {
        setCustomImpliedVolatility(position?.customImpliedVolatility ?? position?.impliedVolatility ?? 0);
    }, [position, isOpen]);

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleSubmitAndCloseDialog = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (position === null || !Number.isFinite(customImpliedVolatility)) {
            return;
        }

        await submitAction(customImpliedVolatility);
        handleClose();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            PaperComponent={DraggableDialogPaper}
            PaperProps={{
                component: "form",
                onSubmit: handleSubmitAndCloseDialog,
            }}
            maxWidth={false}
        >
            <DraggableDialogTitle
                title={`Edit position: ${position?.symbol ?? ""}`}
                onClose={handleClose}
            />
            <DialogContent sx={{ paddingY: "11px", paddingX: "18px" }}>
                <ControlGroup title="Implied volatility">
                    <Stack direction="row" spacing={2}>
                        <NumericField
                            id="currentImpliedVolatility"
                            name="currentImpliedVolatility"
                            label="Current IV"
                            value={position?.impliedVolatility ?? 0}
                            formatter={(value: number) => numberFormatters.format4(
                                value,
                                numberFormatters.hideGroupDelimiterFormatOptions,
                            )}
                            width={100}
                            step={0.0001}
                            disabled
                        />
                        <NumericField
                            id="customImpliedVolatility"
                            name="customImpliedVolatility"
                            label="Custom IV"
                            value={customImpliedVolatility}
                            onValueChange={setCustomImpliedVolatility}
                            formatter={(value: number) => numberFormatters.format4(
                                value,
                                numberFormatters.hideGroupDelimiterFormatOptions,
                            )}
                            width={100}
                            step={0.0001}
                        />
                    </Stack>
                </ControlGroup>
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ fontWeight: "bold" }}
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <Button
                    sx={{ fontWeight: "bold" }}
                    size="small"
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={position === null || !Number.isFinite(customImpliedVolatility)}
                >
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    );
}
